import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  Plus, Search, Video, FileText, CheckCircle2, Clock, AlertCircle, BanIcon,
  ClipboardCheck, ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const STATUS = {
  pendiente:  { label: "Pendiente",  cls: "bg-[#FAECE7] text-[#A14620] border-transparent", icon: Clock },
  en_curso:   { label: "En curso",   cls: "bg-[#FAECE7] text-[#D85A30] border-transparent", icon: Video },
  finalizado: { label: "Finalizada", cls: "bg-[#E6F5EF] text-[#1D9E75] border-transparent", icon: CheckCircle2 },
  cancelado:  { label: "Cancelada",  cls: "bg-[#F1EFE7] text-[#7A786F] border-transparent", icon: BanIcon },
};

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, telematico: 0, videoperitacion: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState(""); // debounced para no martillear el backend
  const [kindFilter, setKindFilter] = useState("all");
  const [page, setPage] = useState(1);
  const LIMIT = 20;
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canSeeAll = user?.role === "admin" || user?.role === "superadmin";

  // Debounce de búsqueda 300 ms
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Resetea a la página 1 cuando cambia filtro o búsqueda
  useEffect(() => { setPage(1); }, [kindFilter, qDeb]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (kindFilter !== "all") params.kind = kindFilter;
      if (qDeb) params.q = qDeb;
      const r = await api.get("/siniestros", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      if (r.data.counts) setCounts(r.data.counts);
    } finally {
      setLoading(false);
    }
  };

  // Recarga al cambiar página, filtro, búsqueda o location.
  useEffect(() => { load(); }, [location.key, page, kindFilter, qDeb]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const filtered = items; // El backend ya filtra y pagina.
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const fromN = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const toN = Math.min(page * LIMIT, total);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#2C2C2A]">Peritaciones</h1>
          <p className="text-[#7A786F] mt-1">Historial y sesiones en curso.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="rounded-[12px] border-[#E5E2D8]" data-testid="new-telematico-btn">
            <Link to="/telematico/new"><ClipboardCheck className="h-4 w-4 mr-1.5" /> Nuevo telemático</Link>
          </Button>
          <Button asChild className="btn-eks-primary rounded-[12px]" data-testid="new-siniestro-btn">
            <Link to="/siniestros/new"><Plus className="h-4 w-4 mr-1.5" /> Nueva peritación</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Total" value={counts.total} icon={FileText} />
        <KPI label="Videoperitación" value={counts.videoperitacion} icon={Video} accent />
        <KPI label="Telemático" value={counts.telematico} icon={ClipboardCheck} info />
      </div>

      <div className="bg-white border border-[#E5E2D8] rounded-[14px] overflow-hidden">
        <div className="p-4 border-b border-[#E5E2D8] flex items-center gap-3 flex-wrap">
          <Search className="h-4 w-4 text-[#7A786F]" />
          <Input
            placeholder={canSeeAll
              ? "Buscar por expediente, asegurado, teléfono o perito…"
              : "Buscar por expediente, asegurado o teléfono…"}
            value={q} onChange={(e) => setQ(e.target.value)}
            className="border-0 focus-visible:ring-0 shadow-none px-0 h-8 bg-transparent flex-1 min-w-[200px]"
            data-testid="search-input"
          />
          {/* Filtro por tipo de expediente */}
          <div className="inline-flex items-center bg-[#FBFAF6] border border-[#E5E2D8] rounded-full p-0.5 text-xs">
            {[
              { id: "all",             label: `Todos · ${counts.total}` },
              { id: "videoperitacion", label: `Videoperitación · ${counts.videoperitacion}`, icon: Video },
              { id: "telematico",      label: `Telemático · ${counts.telematico}`, icon: ClipboardCheck },
            ].map((opt) => {
              const active = kindFilter === opt.id;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.id}
                  onClick={() => setKindFilter(opt.id)}
                  className={`px-3 h-7 rounded-full inline-flex items-center gap-1.5 font-medium transition-colors ${active ? "bg-white text-[#D85A30] shadow-sm" : "text-[#7A786F] hover:text-[#2C2C2A]"}`}
                  data-testid={`filter-kind-${opt.id}`}
                >
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FBFAF6] hover:bg-[#FBFAF6] border-[#E5E2D8]">
              <TableHead className="text-[#7A786F] font-medium w-[150px]">Tipo</TableHead>
              <TableHead className="text-[#7A786F] font-medium">Expediente</TableHead>
              <TableHead className="text-[#7A786F] font-medium">Asegurado</TableHead>
              {canSeeAll && <TableHead className="text-[#7A786F] font-medium">Perito / Tramitador</TableHead>}
              <TableHead className="text-[#7A786F] font-medium">Estado</TableHead>
              <TableHead className="text-[#7A786F] font-medium">Envío</TableHead>
              <TableHead className="text-[#7A786F] font-medium">Creada</TableHead>
              <TableHead className="text-right text-[#7A786F] font-medium">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canSeeAll ? 8 : 7} className="py-10 text-center text-[#7A786F]">Cargando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={canSeeAll ? 8 : 7} className="py-12 text-center text-[#7A786F]">
                {items.length === 0
                  ? "Aún no hay peritaciones. Empieza creando la primera."
                  : "No hay expedientes que coincidan con el filtro."}
              </TableCell></TableRow>
            ) : filtered.map((s) => {
              const st = STATUS[s.status] || STATUS.pendiente;
              const Icon = st.icon;
              const isTel = s.kind === "telematico";
              return (
                <TableRow key={s.id} className="row-hover cursor-pointer border-[#E5E2D8]" data-testid={`row-${s.expediente_num}`}>
                  <TableCell onClick={() => nav(`/siniestros/${s.id}`)}>
                    {isTel ? (
                      <Badge variant="outline" className="bg-[#EAF1FB] text-[#2E5AAC] border-transparent rounded-full" data-testid="kind-badge-telematico">
                        <ClipboardCheck className="h-3 w-3 mr-1" /> Telemático
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-[#FBF0E9] text-[#A14620] border-transparent rounded-full" data-testid="kind-badge-videoperitacion">
                        <Video className="h-3 w-3 mr-1" /> Videoperitación
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-[#2C2C2A]" onClick={() => nav(`/siniestros/${s.id}`)}>
                    {s.expediente_num}
                    {s.policy_template_name && s.policy_template_name !== "Otra póliza" && (
                      <div className="text-[10px] text-[#7A786F] uppercase tracking-wide mt-0.5">{s.policy_template_name}</div>
                    )}
                  </TableCell>
                  <TableCell onClick={() => nav(`/siniestros/${s.id}`)}>
                    <div>{s.asegurado_nombre}</div>
                    {!isTel && s.asegurado_telefono && (
                      <div className="text-[10px] text-[#7A786F] font-mono">{s.asegurado_telefono}</div>
                    )}
                  </TableCell>
                  {canSeeAll && (
                    <TableCell onClick={() => nav(`/siniestros/${s.id}`)} data-testid={`perito-cell-${s.expediente_num}`}>
                      {s.perito_name ? (
                        <>
                          <div className="text-sm text-[#2C2C2A]">{s.perito_name}</div>
                          {s.perito_email && <div className="text-[10px] text-[#7A786F]">{s.perito_email}</div>}
                        </>
                      ) : (
                        <span className="text-[#7A786F] italic text-xs">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant="outline" className={`${st.cls} font-medium rounded-full`}>
                      <Icon className="h-3 w-3 mr-1" /> {st.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {isTel ? (
                      <span className="inline-flex items-center text-xs text-[#7A786F]">
                        — sin envío
                      </span>
                    ) : s.invite_sent ? (
                      <span className="inline-flex items-center text-xs text-[#1D9E75]">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {s.canal === "whatsapp" ? "WhatsApp" : "SMS"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs text-[#A14620]" title={s.invite_error || ""}>
                        <AlertCircle className="h-3.5 w-3.5 mr-1" /> No enviado
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-[#7A786F]">
                    {fmtDateTime(s.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {isTel ? (
                      <Button size="sm" variant="outline" className="border-[#E5E2D8] hover:bg-[#EAF1FB] rounded-[10px]" onClick={() => nav(`/siniestros/${s.id}`)} data-testid={`open-tel-${s.expediente_num}`}>
                        <ChevronRight className="h-3.5 w-3.5 mr-1" /> Abrir
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-[#E5E2D8] hover:bg-[#FAECE7] rounded-[10px]" onClick={() => nav(`/call/${s.id}`)} data-testid={`enter-call-${s.expediente_num}`}>
                        <Video className="h-3.5 w-3.5 mr-1" /> Entrar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {/* Pie con paginación 20 en 20 */}
        <div className="flex items-center justify-between p-3 border-t border-[#E5E2D8] bg-[#FBFAF6] text-xs text-[#7A786F]" data-testid="pagination-bar">
          <div>
            {total === 0
              ? "Sin resultados"
              : `Mostrando ${fromN}–${toN} de ${total}`}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="outline" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-[10px] border-[#E5E2D8] h-7 px-2"
              data-testid="pagination-prev"
            >Anterior</Button>
            <span className="px-2 font-medium text-[#2C2C2A]" data-testid="pagination-info">
              Página {page} / {totalPages}
            </span>
            <Button
              size="sm" variant="outline" disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-[10px] border-[#E5E2D8] h-7 px-2"
              data-testid="pagination-next"
            >Siguiente</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent, success, info }) {
  const iconBg = success
    ? "bg-[#E6F5EF] text-[#1D9E75] border-transparent"
    : info
      ? "bg-[#EAF1FB] text-[#2E5AAC] border-transparent"
      : accent
        ? "bg-[#FAECE7] text-[#D85A30] border-transparent"
        : "bg-[#F1EFE7] text-[#7A786F] border-transparent";
  return (
    <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#7A786F] font-medium">{label}</div>
        <div className={`h-8 w-8 rounded-[10px] border flex items-center justify-center ${iconBg}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-heading text-3xl font-bold text-[#2C2C2A] mt-2">{value}</div>
    </div>
  );
}
